
import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import styles from './index.module.scss';
import ChatRoom from 'components/ChatRoom';
import classnames from 'classnames';
import DialogModule from 'components/Dialogs/'
import StudentRaiseButton from './StudentRaiseButton'
import { Roles } from 'constants/role';
import { EmojiEvent, ToolsEvent, ReaderEvent } from 'events/EventTypes';
import { EventBus } from 'events/EventBus';
import { useStore, StoreTypes } from 'context';
import { DeviceType } from 'constants/chimeTypes'
import SelectComponent from 'components/SelectComponent'
import VolumeMeter from 'components/Tools/VolumeMeter'
import Icon from 'components/Icon';
import SynchronousReader from 'components/Tools/SynchronousReader';
import * as types from 'constants/actionTypes';
import { useChimeError } from 'customHooks/chimeError'
import {
  RemoteVideo,
  LocalVideo,
  useVideoInputs,
  useAudioInputs,
  useAudioOutputs,
  useLocalVideo,
  useToggleLocalMute,
  useLocalAudioOutput,
  useRosterState,
  useMeetingManager,
  useRemoteVideoTileState,
  useSelectVideoQuality,
  useAudioVideo,
  useContentShareControls,
  Notification,
  Severity,
  useNotificationState,
  useNotificationDispatch,
  NotificationProvider,
  NotificationGroup,
  ActionType,
  SignalStrength
} from 'amazon-chime-sdk-component-library-react';

export const StudentVideo = React.memo(({ isFull }) => {
  const [attendeeId, setAttendeeId] = useState('');
  const { roster } = useRosterState();
  const { attendeeIdToTileId } = useRemoteVideoTileState();
  const audioVideo = useAudioVideo();
  const [signalStrength, setSignalStrength] = useState('');

  const bindAttendeeCallback = (audioVideo, attendeeId) => {
    audioVideo.realtimeSubscribeToVolumeIndicator(
      attendeeId,
      (attendeeId, volume, muted, signalStrength, externalUserId) => {
        if (signalStrength === null) {
          // signalStrength has not changed
          return;
        }

        setSignalStrength(signalStrength);
      }
    );
  }


  useEffect(() => {

    const student = Object.values(roster).find(item => item.externalUserId.split("$")[1] === Roles.TUTOR_USER);

    if (student) {
      setAttendeeId(student.chimeAttendeeId)
    }

    if (!student || !audioVideo || !student.chimeAttendeeId) {
      return;
    }

    bindAttendeeCallback(audioVideo, student.chimeAttendeeId);

  }, [roster, audioVideo])


  return attendeeId && attendeeIdToTileId[attendeeId] ?
    <>
      <RemoteVideo tileId={attendeeIdToTileId[attendeeId]} />
      {
        !isFull && <VolumeMeter className={styles.volumeMeter} width={50} height={15} attendeeId={attendeeId} />
      }

      <SignalStrength className={classnames(styles.signal, GetSignalStrengthCssName(signalStrength))} />

    </>
    : null
})

export const StudentFullVideo = React.memo(() => {
  const [attendeeId, setAttendeeId] = useState('');
  const { roster } = useRosterState();
  const { attendeeIdToTileId } = useRemoteVideoTileState();
  const audioVideo = useAudioVideo();
  const [signalStrength, setSignalStrength] = useState('');

  const bindAttendeeCallback = (audioVideo, attendeeId) => {
    audioVideo.realtimeSubscribeToVolumeIndicator(
      attendeeId,
      (attendeeId, volume, muted, signalStrength, externalUserId) => {
        if (signalStrength === null) {
          // signalStrength has not changed
          return;
        }

        setSignalStrength(signalStrength);
      }
    );
  }


  useEffect(() => {

    const student = Object.values(roster).find(item => item.externalUserId.split("$")[1] === Roles.TUTOR_USER);

    if (student) {
      setAttendeeId(student.chimeAttendeeId)
    }
    if (!student || !audioVideo || !student.chimeAttendeeId) {
      return;
    }
    bindAttendeeCallback(audioVideo, student.chimeAttendeeId);

  }, [roster, audioVideo])

  return attendeeId && attendeeIdToTileId[attendeeId] ?
    <>
      <RemoteVideo tileId={attendeeIdToTileId[attendeeId]} />
      {/* <SignalStrength className={classnames(styles.signal, GetSignalStrengthCssName(signalStrength))} /> */}

    </>
    : null
})


const GetSignalStrengthCssName = (signalStrength) => {
  if (signalStrength == 1)
    return styles.signalHigh;
  else if (signalStrength == 0)
    return styles.signalLow;
  else
    return styles.signalMedium
};

const StudentToolsControlList = React.memo(() => {
  const [{ toolsIsControl }] = useStore(StoreTypes.reader);
  const { roster } = useRosterState();

  const [userId, setUserId] = useState('');
  const [teacher, setTeacherId] = useState('');

  const studentToolsControl = useCallback(({ userId }) => {
    EventBus.emit({ event: ToolsEvent.ToolsIsControl, payload: { userId } })
  }, [])

  useEffect(() => {
    const student = Object.values(roster).find(item => item.externalUserId.split("$")[1] === Roles.TUTOR_USER);
    const teacher = Object.values(roster).find(item => item.externalUserId.split("$")[1] === Roles.TUTOR);

    if (student) {
      setUserId(student.externalUserId)
    }

    if (teacher) {
      setTeacherId(teacher.externalUserId)
    }

  }, [roster])

  return userId ? (
    <div
      className={styles.toolSwitch}
      onClick={() => studentToolsControl({ userId: toolsIsControl ? userId : teacher })}>
      {
        toolsIsControl ? <Icon name='OpenTools' /> : <Icon name='CloseTools' />
      }
    </div>
  ) : null
})

const LiveStreamMasterPanel = ({ isShow, channelName, clientId, userId, role }) => {

  const [{ rewardInfo, isFullVideo }, courseDispatch] = useStore(StoreTypes.course);
  const { fireworks, good, like, trophy } = rewardInfo;

  const meetingManager = useMeetingManager();

  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { devices: videoInputDevices } = useVideoInputs();
  const selectVideoQuality = useSelectVideoQuality();
  const audioVideo = useAudioVideo();
  const { devices: audioInputDevices } = useAudioInputs();
  const { devices: audioOutputDevices } = useAudioOutputs();

  const { muted, toggleMute } = useToggleLocalMute();
  const { isAudioOn, toggleAudio } = useLocalAudioOutput();

  const selfAttendeeId = meetingManager.configuration.credentials.attendeeId;

  const { roster } = useRosterState();
  const attendees = Object.values(roster);
  const studentFullVideoRef = useRef();

  const [students, setStudents] = useState(null);
  const sendEmojiCode = useCallback((code) => {
    EventBus.emit({ event: EmojiEvent.AddUpEmojiEvent, payload: { emoji: code } })
  }, [])
  const { processDeviceError, showNotification } = useChimeError();
  const { notifications } = useNotificationState();
  const dispatch = useNotificationDispatch();

  const notificationItems =
    notifications.map(({ id, ...rest }) => (
      <Notification
        key={id}
        {...rest}
        onClose={() => dispatch({ type: ActionType.REMOVE, payload: id })}
      />
    ));

  useEffect(() => {
    let audioError = false;
    let videoError = false;
    if (audioInputDevices.length == 0 || audioInputDevices.some(item => item.label.length == 0))
      audioError = true;
    else if (videoInputDevices.length == 0 || videoInputDevices.some(item => item.label.length == 0))
      videoError = true;

    if (audioError) {
      processDeviceError('麥克風', "PermissionDeniedError");
    }
    if (videoError) {
      processDeviceError('攝影機', "PermissionDeniedError");
    }
  }, [audioInputDevices, videoInputDevices])

  useEffect(() => {
    const attendeeChangedObserver = async (presentAttendeeId, present, externalUserId) => {
      let roleType;
      if (externalUserId)
        roleType = externalUserId.split("$")[1];
      if (roleType == Roles.TUTOR_USER) {
        if (present)
          showNotification(Severity.INFO, "學生已上線")
        else
          showNotification(Severity.WARNING, "學生已離線", true)
      }

    };
    if (audioVideo) {
      //  audioVideo.addObserver(myObserver);
      audioVideo.realtimeSubscribeToAttendeeIdPresence(attendeeChangedObserver);
    }
  }, [audioVideo]);

  useEffect(() => {
    const studentsInfo = Object.values(roster).find(item => item.externalUserId.split("$")[1] === Roles.TUTOR_USER);
    setStudents(studentsInfo)
  }, [students, roster])

  useEffect(() => {
    (async () => {
      if (!isVideoEnabled) {
        try {
          await toggleVideo();
        }
        catch (error) {
          processDeviceError('攝影機', error);
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    isFullVideo ? selectVideoQuality('720p') : audioVideo.chooseVideoInputQuality(320, 240, 10, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullVideo])

  const formatDeviceValue = useCallback(devices => devices.map(item => (
    { label: item.label, value: item.deviceId }
  )
  ), [])

  const { toggleContentShare, isLocalUserSharing, isLocalShareLoading } = useContentShareControls();

  const videoFullScreenHandler = (isFull) => {
    EventBus.emit({ event: ReaderEvent.sendFullVideoEvent, payload: { isFullVideo: isFull } });
    if (isFull && (isLocalUserSharing || isLocalShareLoading)) {
      toggleContentShare();
    }
  }

  const toggleContentShareHandler = () => {
    toggleContentShare();
    if (isFullVideo && !(isLocalUserSharing || isLocalShareLoading)) {
      videoFullScreenHandler(false)
    }
  }

  const onVideoInputChange = async (deviceId) => {
    if (!audioVideo || !meetingManager)
      return;
    //先關閉再開啟
    try {
      await audioVideo.chooseVideoInputDevice(null);
      await audioVideo.chooseVideoInputDevice(deviceId);
      audioVideo.startLocalVideoTile();
    }
    catch (error) {
      processDeviceError('攝影機', error);
    }
  }

  const onAudioInputChange = async (deviceId) => {
    await meetingManager.selectAudioInputDevice(deviceId).catch((error) => {
      processDeviceError('麥克風', error);
    });
  }

  return (
    <div className={classnames(styles.discussionPanel, styles.wrapper)}>
      <div id="content" className={styles.chatContent}>
        <div className={styles.toolsControls}>
          {
            videoInputDevices.length > 0 && (
              <div className={styles.localVideoSelect}>
                <div onClick={toggleVideo}>
                  {
                    isVideoEnabled ?
                      <Icon name="OpenVideo" /> :
                      <Icon name="CloseVideo" />
                  }
                </div>
                {
                  videoInputDevices.some(item => item.label.length == 0) ?
                    <span>請檢查攝影機權限</span>
                    :
                    <SelectComponent
                      type={DeviceType.VideoDevice}
                      optionsList={formatDeviceValue(videoInputDevices)}
                      defaultSelectText={videoInputDevices[0].label}
                      onChange={onVideoInputChange}
                    />
                }
              </div>
            )
          }
          {
            audioInputDevices.length > 0 &&
            <div className={styles.localSound}>
              <div onClick={toggleMute}>
                {
                  muted ?
                    <Icon name="CloseAudioInput" /> :
                    <Icon name="OpenAudioInput" />
                }
              </div>
              {
                audioInputDevices.some(item => item.label.length == 0) ?
                  <span>請檢查麥克風權限</span>
                  :
                  <SelectComponent
                    type={DeviceType.AudioInputDevice}
                    optionsList={formatDeviceValue(audioInputDevices)}
                    defaultSelectText={audioInputDevices[0] && audioInputDevices[0].label}
                    onChange={onAudioInputChange}
                  />
              }
            </div>
          }
          {
            audioOutputDevices.length > 0 &&
            <div className={styles.localAudio}>
              <div onClick={toggleAudio}>
                {
                  isAudioOn ?
                    <Icon name="OpenAudioOutput" /> :
                    <Icon name="CloseAudioOutput" />
                }
              </div>
              <SelectComponent
                type={DeviceType.AudioOutputDevice}
                optionsList={formatDeviceValue(audioOutputDevices)}
                defaultSelectText={audioOutputDevices[0] && audioOutputDevices[0].label}
                onChange={meetingManager.selectAudioOutputDevice}
              />
            </div>
          }
          <div className={styles.toolControl}>
            <StudentToolsControlList />
            {/* <SynchronousReader /> */}
            <button
              className={styles.shareButton}
              onClick={toggleContentShareHandler}
              disabled={isLocalShareLoading}
            >
              {
                isLocalShareLoading ?
                  '螢幕分享中...' : isLocalUserSharing ?
                    '停止螢幕分享' : '螢幕分享'
              }
            </button>
          </div>
        </div>

        <div className={classnames(styles.localVideo, { [styles.localVideoHide]: isFullVideo, [styles.localVideoBG]: isFullVideo })}>
          {
            !isFullVideo && <StudentVideo isFull={isFullVideo} />
          }
          {
            <div
              className={classnames(styles.offFullVideoBtn, { [styles.offFullVideoBtnHide]: !isFullVideo })}
              onClick={() => videoFullScreenHandler(false)}
            >
              <Icon name="OffFullVideoOver" />
              <Icon name="OffFullVideoOut" />
            </div>
          }
          {
            students &&
            <div className={classnames(styles.fullVideoBtn, { [styles.fullVideoBtnHide]: isFullVideo })}
              onClick={() => videoFullScreenHandler(true)}>
              <Icon name="FullVideoOver" />
              <Icon name="FullVideoOut" />
            </div>
          }
        </div>
        <div className={styles.localVideo}>
          <LocalVideo />
          <VolumeMeter className={styles.volumeMeter} width={50} height={15} attendeeId={selfAttendeeId} />
        </div>
        {
          role === Roles.TUTOR &&
          <div className={styles.emojiCounts}>
            <div className={styles.emojiCount}>
              <div><Icon name="FireworksIcon" /></div>
              <div className={styles.countText}>{fireworks && fireworks}</div>
            </div>
            <div className={styles.emojiCount}>
              <div><Icon name="GoodIcon" /></div>
              <div className={styles.countText}>{good && good}</div>
            </div>
            <div className={styles.emojiCount}>
              <div><Icon name="LikeIcon" /></div>
              <div className={styles.countText}>{like && like}</div>
            </div>
            <div className={styles.emojiCount}>
              <div><Icon name="TrophyIcon" /></div>
              <div className={styles.countText}>{trophy && trophy}</div>
            </div>
          </div>
        }
        <div className={classnames(styles.chatRoomWrapper, { [styles.tutor]: role === Roles.TUTOR })}>
          <ChatRoom />
        </div>
        <div >
          <NotificationGroup>
            {notificationItems}
          </NotificationGroup>
        </div>
      </div>
    </div>
  )
}

export default LiveStreamMasterPanel;
